import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Paper, Skeleton, TextField, Typography } from '@mui/material';
import { showAxiosMessageError } from '../../utils/error-message.util';
import { useSnackbars } from '../../hooks/snackbar.hook';
import { ContactsDto } from '../../services/dtos/contacts.dto';
import { getContacts } from '../../services/contact.service';
import ContactsUi from '../../components/userInterfaces/contacts/contacts.ui';
import { ContactDto } from '../../services/dtos/contact.dto';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SendMailDialogForm from '../../components/forms/sendMail/sendMailDialog.form';
import './contacts.page.scss';

const matchesSearch = (section: ContactsDto, contact: ContactDto, normalizedSearch: string) =>
    [
        section.category,
        contact.firstName,
        contact.lastName,
        contact.function,
        contact.email,
        contact.landphone,
        contact.cellphone,
    ].some(value => value?.toLowerCase().includes(normalizedSearch));

/**
 * Page de compte de l'utilisateur
 * @constructor
 */
export default function ContactsPage() {
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    // Données de l'utilisateur nécessaires pour la page en cours
    const [contacts, setContacts] = useState<ContactsDto[]>();
    const [search, setSearch] = useState('');
    const deferredSearch = useDeferredValue(search);
    // Gestion du dialog d'envoi de mail
    const [selectedContact, setSelectedContact] = useState<ContactDto>();

    const handleClickOpen = (contact: ContactDto) => {
        setSelectedContact(contact);
    };

    const handleClose = () => {
        setSelectedContact(undefined);
    };

    useEffect(() => {
        let isMounted = true;

        //Récupération de l'utilisateur courant
        getContacts()
            .then(result => {
                isMounted && setContacts(result);
            })
            .catch(error => {
                console.error('Erreur lors de la récupération des contacts');
                showAxiosMessageError(error, showMessage);
            });

        //unamount
        return () => {
            isMounted = false;
        };
    }, [showMessage]);

    const normalizedSearch = deferredSearch.trim().toLowerCase();
    const visibleSections = useMemo(
        () => contacts?.filter(section => (section.contacts?.length ?? 0) > 0) ?? [],
        [contacts]
    );
    const filteredContacts = useMemo(() => {
        if (!normalizedSearch) {
            return visibleSections;
        }

        return visibleSections
            .map(section => ({
                ...section,
                contacts: section.contacts?.filter(contact => matchesSearch(section, contact, normalizedSearch)),
            }))
            .filter(section => (section.contacts?.length ?? 0) > 0);
    }, [normalizedSearch, visibleSections]);

    const filteredContactsCount = useMemo(
        () => filteredContacts.reduce((count, section) => count + (section.contacts?.length ?? 0), 0),
        [filteredContacts]
    );

    let content = <Skeleton variant="rounded" height={320} className="contactsPage__skeleton" />;
    if (contacts) {
        content =
            filteredContactsCount > 0 ? (
                <ContactsUi contacts={filteredContacts} handleClickOpen={handleClickOpen} />
            ) : (
                <Paper className="contactsPage__emptyState">
                    <Typography variant="h5">Aucun contact trouvé</Typography>
                    <Typography variant="body2">Essayez avec un autre nom, une fonction ou une catégorie.</Typography>
                </Paper>
            );
    }

    //Conditionnement de la vue, plusieurs interfaces disponibles
    return (
        <div className="contactsPage">
            <div className="contactsPage__header">
                <Typography variant="h2">Mes contacts</Typography>
                <Typography variant="body1" className="contactsPage__subtitle">
                    Retrouvez rapidement les interlocuteurs utiles et contactez-les en quelques clics.
                </Typography>
            </div>
            <div className="contactsPage__toolbar">
                <TextField
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    label="Rechercher un contact"
                    placeholder="Nom, fonction, e-mail, téléphone, catégorie..."
                    size="small"
                    fullWidth
                    className="contactsPage__search"
                    InputProps={{
                        startAdornment: <SearchOutlinedIcon className="contactsPage__searchIcon" />,
                    }}
                />
            </div>
            <div className="contactsPage__panel">{content}</div>
            <SendMailDialogForm
                open={Boolean(selectedContact?.email)}
                emails={selectedContact?.email ? [selectedContact.email] : []}
                handleClose={handleClose}
            />
        </div>
    );
}
